'use client';

import { createContext, use, useLayoutEffect, useMemo, useSyncExternalStore, type ReactNode } from 'react';
import { resolveTheme, type ResolvedTheme, type ThemePreference } from '@/contexts/theme/shared';
import { createThemeStore } from '@/contexts/theme/store';

interface ThemeState {
    /** 用户偏好，与公开页面强制展示的颜色模式不同。 */
    theme: ThemePreference;
    resolvedTheme: ResolvedTheme;
    setTheme: (theme: ThemePreference) => void;
}

interface ThemeProviderProps {
    /** 应用子树。 */
    children: ReactNode;
    /** 由应用策略传入；公开区域为 light，不覆写保存的偏好。 */
    forcedTheme?: ResolvedTheme;
    /** 偏好存储位置，由区域策略给出。 */
    storageKey: string;
}

const ThemeContext = createContext<ThemeState | null>(null);

export function ThemeProvider({ children, forcedTheme, storageKey }: ThemeProviderProps) {
    // 每个偏好存储对应一个 store；区域切到另一个 key 时换 store，访客站点不读写主站账号的主题。
    // store 只缓存可从 localStorage 重建的值，重建不丢状态。
    const store = useMemo(() => createThemeStore(storageKey), [storageKey]);
    const { theme, systemTheme, hydrated } = useSyncExternalStore(store.subscribe, store.getSnapshot, store.getServerSnapshot);

    const resolvedTheme = resolveTheme(theme, systemTheme, forcedTheme);
    useLayoutEffect(() => {
        // 非强制模式保留首帧脚本的结果，不能在偏好尚未读取时闪回亮色。
        if (!hydrated && !forcedTheme) return;
        const root = document.documentElement;
        root.classList.remove('light', 'dark');
        root.classList.add('theme-zinc', resolvedTheme);
        root.style.colorScheme = resolvedTheme;
    }, [forcedTheme, hydrated, resolvedTheme]);

    const setTheme = store.setTheme;
    const value = useMemo(() => ({ theme, resolvedTheme, setTheme }), [resolvedTheme, setTheme, theme]);
    return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
}

export function useTheme(): ThemeState {
    const theme = use(ThemeContext);
    if (!theme) throw new Error('Home ThemeProvider is required');
    return theme;
}
